/**
 * Link Coordination Model Tool
 *
 * Links a coordination model (.ifc) into the project for clash review,
 * mirroring the BIM Insert > Link panel entry. The file goes through the
 * real web-ifc pipeline (parse → bake asset → scene renders the actual
 * geometry). Navisworks containers (.nwd/.nwc) and Revit binaries (.rvt)
 * cannot be decoded in this build — they are refused with an explicit
 * "export to IFC" message instead of leaving a schematic footprint box.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager, LinkInstance } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import { buildIfcLinkPayload, IfcLinkPayload } from '../services/ifc/IfcLinkService';
import { CadLinkStore } from '../services/cad/CadLinkStore';
import i18n from '../i18n';

export interface CoordinationLinkGeometryData {
    linkId: string;
    origin: [number, number, number];
    scale: [number, number, number];
    rotation: [number, number, number];
    /** Real model extents in metres. */
    boxSize: [number, number, number];
    /** Baked model asset — the scene streams the real geometry from it. */
    ifc: {
        assetId: string;
        schema: string;
        productCount: number;
        source: string;
    };
}

interface CoordinationLinkElement extends Element {
    geometryData: CoordinationLinkGeometryData;
}

export class LinkCoordinationModelTool extends BaseTool {
    id = 'link_coordination_model';
    name = 'Link Coordination Model';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    onActivate(): void {
        // Dialog-driven command: pick a coordination model. Only IFC is
        // offered — it is the one coordination format this build can read.
        void this.pickFile('.ifc').then(async (file) => {
            if (file) {
                await this.linkIfcModel(file);
            }
            this.exitToSelect();
        });
    }

    /**
     * Full link flow with a real File: parse through the web-ifc engine,
     * then bind the wrapper element so Manage Links can act on it.
     */
    public async linkIfcModel(file: File): Promise<LinkInstance | null> {
        const link = this.linkManager.linkFile('Coordination', file.name, {
            positioning: 'OriginToOrigin',
            size: file.size
        });

        this.notify(i18n.t('tools.notify.importing', { fileName: file.name }));
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const payload = await buildIfcLinkPayload(file.name, bytes);
            const element = this.createLinkElement(link, payload);
            this.linkManager.setDocumentId(link.id, element.id);
            // Session source so Manage Links Reload re-parses the same file.
            CadLinkStore.setSource(element.id, file, file.name);
            this.notify(
                i18n.t('tools.notify.ifcLinkParsed', { fileName: file.name, count: payload.productCount, schema: payload.schema }),
                'success'
            );
            return link;
        } catch (err) {
            // No readable model → no link registration either.
            this.linkManager.removeLink(link.id);
            this.notify(i18n.t('tools.notify.ifcLinkFailed', { message: (err as Error).message }), 'error');
            return null;
        }
    }

    /**
     * Programmatic entry (macros/tests) with a known path. Only IFC files
     * carry parseable content; anything else is refused, never faked.
     */
    public async executeLink(filePath: string, size = 0, bytes?: Uint8Array): Promise<LinkInstance | null> {
        if (!/\.ifc$/i.test(filePath)) {
            this.notify(
                i18n.t('tools.notify.coordFormatUnsupported'),
                'error'
            );
            return null;
        }
        if (!bytes) {
            this.notify(i18n.t('tools.notify.ifcLinkFailed', { message: `${filePath}: file bytes not available for parsing.` }), 'error');
            return null;
        }
        const link = this.linkManager.linkFile('Coordination', filePath, {
            positioning: 'OriginToOrigin',
            size
        });
        try {
            const payload = await buildIfcLinkPayload(filePath, bytes);
            const element = this.createLinkElement(link, payload);
            this.linkManager.setDocumentId(link.id, element.id);
            return link;
        } catch (err) {
            this.linkManager.removeLink(link.id);
            this.notify(i18n.t('tools.notify.ifcLinkFailed', { message: (err as Error).message }), 'error');
            return null;
        }
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: nothing to place interactively.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createLinkElement(link: LinkInstance, payload: IfcLinkPayload): CoordinationLinkElement {
        const size: [number, number, number] = [
            Math.max(payload.bbox.max[0] - payload.bbox.min[0], 1),
            Math.max(payload.bbox.max[1] - payload.bbox.min[1], 1),
            Math.max(payload.bbox.max[2] - payload.bbox.min[2], 1),
        ];

        const linkElement: CoordinationLinkElement = {
            id: uuidv4(),
            category: 'Other Links',
            type: 'Coordination Model: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Status': link.status,
                'Role': 'Coordination',
                'Products': String(payload.productCount),
            },
            constraints: [],
            geometryData: {
                linkId: link.id,
                origin: [0, 0, 0],
                scale: [1, 1, 1],
                rotation: [0, 0, 0],
                boxSize: size,
                ifc: {
                    assetId: payload.assetId,
                    schema: payload.schema,
                    productCount: payload.productCount,
                    source: link.path,
                },
            }
        };

        this.context?.bimStore.addElement(linkElement as any);
        return linkElement;
    }
}
